/**
 * Beta Authentication Service
 * Allows hover.redheli.com users to authenticate using their beta.redheli.com credentials
 * This service acts as a bridge between Supabase Auth (hover) and Passport.js (beta)
 */

interface BetaAuthResponse {
  success: boolean;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    isAdmin?: boolean;
  };
  message?: string;
  error?: string;
  sessionId?: string;
}

interface BetaUserStatus {
  authenticated: boolean;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    authStrategy?: 'local' | 'saml' | 'oauth';
  };
}

export class BetaAuthService {
  private betaUrl: string;
  private sessionCookie: string | null = null;

  constructor() {
    // Use local proxy on localhost to avoid CORS issues
    if (window.location.hostname === 'localhost') {
      this.betaUrl = 'http://localhost:3000/api/beta-auth';
    } else {
      this.betaUrl = import.meta.env.VITE_BETA_URL || 'https://beta.redheli.com';
    }
  }

  /**
   * Authenticate user against beta.redheli.com backend
   */
  async authenticateWithBeta(emailOrUsername: string, password: string): Promise<BetaAuthResponse> {
    try {
      // Determine the correct endpoint based on whether we're using proxy
      const loginUrl = window.location.hostname === 'localhost' 
        ? `${this.betaUrl}/login`  // Proxy endpoint
        : `${this.betaUrl}/api/auth/login`; // Direct to beta
      
      console.log('🌐 BetaAuthService: Attempting login to', loginUrl);
      console.log('🌐 Origin:', window.location.origin);
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          login: emailOrUsername,
          password: password
        })
      });

      console.log('🌐 Response status:', response.status);
      console.log('🌐 Response headers:', response.headers);
      
      const data = await response.json();
      console.log('🌐 Response data:', data);

      if (response.ok && data.success) {
        // Store session info for subsequent requests
        this.storeSessionInfo(response.headers);
        
        // Transform beta response to our format
        return {
          success: true,
          user: {
            id: data.user?.id || emailOrUsername,
            email: emailOrUsername.includes('@') ? emailOrUsername : data.user?.email,
            firstName: data.user?.firstName,
            lastName: data.user?.lastName,
            isAdmin: data.user?.isAdmin || false
          },
          message: data.message || 'Authentication successful'
        };
      }

      return {
        success: false,
        error: data.error || 'Authentication failed'
      };
    } catch (error) {
      console.error('🚨 Beta authentication error:', error);
      
      // Check if it's a network/CORS error
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('🚨 CORS or network error detected');
        // For development, we might want to skip beta auth
        if (window.location.hostname === 'localhost') {
          console.warn('🚨 Running on localhost - beta auth might be blocked by CORS');
        }
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to connect to beta authentication service'
      };
    }
  }

  /**
   * Check if user has active session on beta
   */
  async checkBetaSession(email: string): Promise<BetaUserStatus> {
    try {
      const userUrl = window.location.hostname === 'localhost'
        ? `${this.betaUrl}/user`  // Proxy endpoint
        : `${this.betaUrl}/api/auth/user`; // Direct to beta
        
      const response = await fetch(userUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.user && data.user.email === email) {
          return {
            authenticated: true,
            user: data.user
          };
        }
      }

      return {
        authenticated: false
      };
    } catch (error) {
      console.error('Beta session check error:', error);
      return {
        authenticated: false
      };
    }
  }

  /**
   * Register new user on beta platform
   */
  async registerOnBeta(userData: {
    name: string;
    email: string;
    username: string;
    password: string;
    phone?: string;
  }): Promise<BetaAuthResponse> {
    try {
      const response = await fetch(`${this.betaUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        credentials: 'include',
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          user: {
            id: data.user?.id || userData.username,
            email: userData.email,
            firstName: userData.name.split(' ')[0],
            lastName: userData.name.split(' ').slice(1).join(' ')
          },
          message: 'Registration successful'
        };
      }

      return {
        success: false,
        error: data.error || 'Registration failed'
      };
    } catch (error) {
      console.error('Beta registration error:', error);
      return {
        success: false,
        error: 'Failed to connect to beta registration service'
      };
    }
  }

  /**
   * Get user's journey status from beta
   */
  async getUserJourneyStatus(email: string): Promise<any> {
    try {
      const response = await fetch(`${this.betaUrl}/api/user/status?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Source': 'hover',
          'X-Request-Platform': 'hover'
        },
        credentials: 'include'
      });

      if (response.ok) {
        return await response.json();
      }

      return null;
    } catch (error) {
      console.error('Failed to get journey status:', error);
      return null;
    }
  }

  /**
   * Logout from beta platform
   */
  async logoutFromBeta(): Promise<void> {
    try {
      await fetch(`${this.betaUrl}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        credentials: 'include'
      });
      
      this.sessionCookie = null;
    } catch (error) {
      console.error('Beta logout error:', error);
    }
  }

  /**
   * Store session information from response headers
   */
  private storeSessionInfo(headers: Headers): void {
    const setCookie = headers.get('set-cookie');
    if (setCookie) {
      // Parse session cookie if present
      const sessionMatch = setCookie.match(/connect\.sid=([^;]+)/);
      if (sessionMatch) {
        this.sessionCookie = sessionMatch[1];
        localStorage.setItem('beta_session_active', 'true');
        localStorage.setItem('beta_session_time', new Date().toISOString());
      }
    }
  }

  /**
   * Check if there's a stored beta session
   */
  hasStoredBetaSession(): boolean {
    const sessionActive = localStorage.getItem('beta_session_active');
    const sessionTime = localStorage.getItem('beta_session_time');
    
    if (sessionActive && sessionTime) {
      // Check if session is less than 8 hours old (beta's session TTL)
      const sessionDate = new Date(sessionTime);
      const now = new Date();
      const hoursDiff = (now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60);
      
      return hoursDiff < 8;
    }
    
    return false;
  }

  /**
   * Clear stored beta session
   */
  clearStoredBetaSession(): void {
    localStorage.removeItem('beta_session_active');
    localStorage.removeItem('beta_session_time');
    this.sessionCookie = null;
  }

  /**
   * Create seamless redirect URL to beta with authentication
   */
  createBetaRedirectUrl(path: string = '/journey', params?: Record<string, string>): string {
    const url = new URL(`${this.betaUrl}${path}`);
    
    // Add return URL
    url.searchParams.append('returnUrl', window.location.origin + '/portal');
    url.searchParams.append('source', 'hover');
    
    // Add any additional params
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }
    
    return url.toString();
  }

  /**
   * Check if a user exists on beta platform (for migration purposes only)
   */
  async checkIfBetaUser(email: string): Promise<{ isBetaUser: boolean; user?: any }> {
    try {
      // This is a lightweight check, not full authentication
      const userStatus = await this.checkBetaSession(email);
      return {
        isBetaUser: userStatus.authenticated,
        user: userStatus.user
      };
    } catch (error) {
      console.log('Beta user check failed:', error);
      return { isBetaUser: false };
    }
  }
}

// Export singleton instance
export const betaAuthService = new BetaAuthService();